import React from 'react';
import { Document, Page, Text, View } from '@react-pdf/renderer';
import { RentalGrowthRentEstimateSource } from '@zg-rentals/ts-rental-growth-api-client';
import StringConstants from '../constants/StringConstants';
import { asCurrency } from '../utils/numberUtils';

import CompsTable from './CompsTable';
import Filters from './Filters';
import Map from './Map';
import RentRangeChart from './RentRangeChart';
import { ZRMLogo } from './SVGs';
import styles from './styles';

import type Address from '../models/Address';
import type { State as ComparablesState } from '../flux/reducers/comparables';
import type { filterLabelsType } from '../utils/filterUtils';
import ListingStripe from './ListingStripe';
import MarketSummary from './MarketSummary';

interface PDFReportProps {
  listing: Address;
  comparablesState: ComparablesState;
  filterLabels: filterLabelsType;
  customPrice: number | null;
  signedMapUrl: string;
}

const PDFReport: React.FunctionComponent<React.PropsWithChildren<React.PropsWithChildren<PDFReportProps>>> = (
  props,
) => {
  const { listing, comparablesState, filterLabels, customPrice, signedMapUrl } = props;
  const { rentZestimate, rentZestimateRangeHigh, rentZestimateRangeLow, rentZestimateSource } = listing;
  const { min, max, items: comparables } = comparablesState;
  const isListingStatsEstimate = rentZestimateSource === RentalGrowthRentEstimateSource.LISTING_STATS;

  const { areaName, beds, summary } = listing?.marketSummary ?? {};
  const {
    medianRent = null,
    monthlyChange = null,
    yearlyChange = null,
    avgDaysOnMarket = null,
    availableRentals = null,
  } = summary ?? {};
  const hasMarketSummary = !!(medianRent || monthlyChange || yearlyChange || avgDaysOnMarket || availableRentals);

  return (
    // @ts-ignore
    <Document>
      {/* @ts-ignore */}
      <Page size="LETTER" style={styles.page}>
        <View style={styles.marginBottomSm}>
          <View style={{ width: '10%' }}>
            <ZRMLogo />
          </View>
          <View style={{ width: '90%' }}></View>
        </View>
        <View>
          <Text style={styles.h1}>{isListingStatsEstimate ? 'Rental Price Report' : 'Rent Zestimate Report'}</Text>
        </View>
        <ListingStripe
          listing={listing}
          customPrice={customPrice}
          rentZestimateRangeLow={rentZestimateRangeLow || min}
          rentZestimateRangeHigh={rentZestimateRangeHigh || max}
        />
        {hasMarketSummary && (
          <MarketSummary
            market={areaName}
            beds={beds}
            medianRent={medianRent}
            monthlyChange={monthlyChange}
            yearlyChange={yearlyChange}
            avgDaysOnMarket={avgDaysOnMarket}
            availableRentals={availableRentals}
          />
        )}
        <View style={styles.marginBottomSm}>
          <Text style={styles.h5}>Your Comparable Properties</Text>
          <Text style={[styles.body, { color: 'grey' }]}>
            Listings are only a sample in your area and based on{' '}
            {customPrice ? (
              `your custom price of ${asCurrency(customPrice)}`
            ) : isListingStatsEstimate ? (
              `the ${StringConstants.COMPARABLE_HOMES_RENT_LABEL.toLowerCase()}`
            ) : (
              <>
                your Rent Zestimate<Text style={styles.bodySup}>®</Text> of {asCurrency(rentZestimate as number)}
              </>
            )}{' '}
            and applied filters.
          </Text>
        </View>
        <Filters filterLabels={filterLabels} />
        <RentRangeChart comparables={comparables} userPropertyPrice={customPrice || (rentZestimate as number)} />
        <Map signedMapUrl={signedMapUrl} />
        <CompsTable comparables={comparables} />
      </Page>
    </Document>
  );
};

export default PDFReport;
